import { useState } from "react";
import { data, redirect, useNavigation } from "react-router";
import type { Route } from "./+types/sign-in";
import { auth, ROLES_REQUIRING_2FA } from "@caratzon/auth";
import { CaratzonLogo } from "~/components/marketing/svg/CaratzonLogo";
import { BrilliantCut } from "~/components/marketing/svg/BrilliantCut";

export function meta() {
  return [
    { title: "Platform Access · Caratzon" },
    { name: "description", content: "Sign in or create a buyer account to access the Caratzon trade catalogue and sourcing platform." },
  ];
}

/** Only allow relative paths starting with / to prevent open redirect */
function sanitizeRedirect(value: string | null): string | null {
  if (!value) return null;
  const trimmed = value.trim();
  // Block protocol-relative URLs, absolute URLs, and control characters
  if (!/^\/[^\s\\]*$/.test(trimmed)) return null;
  if (trimmed.startsWith("//")) return null;
  return trimmed;
}

export async function loader({ request }: Route.LoaderArgs) {
  const url = new URL(request.url);
  const isDesigner = url.searchParams.get("role") === "designer";
  const demoEnabled = process.env.DEMO_MODE === "true";
  const errorParam = url.searchParams.get("error");
  const redirectTo = sanitizeRedirect(url.searchParams.get("redirect"));
  return { isDesigner, demoEnabled, errorParam, redirectTo };
}

export async function action({ request }: Route.ActionArgs) {
  const formData = await request.formData();
  const intent = String(formData.get("intent"));

  const redirectTo = sanitizeRedirect(String(formData.get("redirect") ?? ""));

  if (intent === "sign-in") {
    const email = String(formData.get("email") ?? "");
    const password = String(formData.get("password") ?? "");
    try {
      // Step 1: Validate password
      const authResponse = await (auth as any).api.signInEmail({
        body: { email, password },
        headers: request.headers,
        asResponse: true,
      });
      if (!authResponse.ok) {
        return data({ error: "Invalid email or password. Please try again.", intent }, { status: 401 });
      }

      // Step 2: Check if user role requires 2FA
      const sessionResult = await (auth as any).api.getSession({
        headers: authResponse.headers,
      });
      const role = sessionResult?.user?.role as string | undefined;

      if (role && ROLES_REQUIRING_2FA.includes(role)) {
        // Revoke the session — user must complete 2FA via magic link.
        // Do this before sending the link so no live session exists during the
        // window between revoke and link delivery.
        await (auth as any).api.revokeSession({
          headers: authResponse.headers,
          body: { token: sessionResult.session.token },
        });

        // Send magic link — use a dedicated try/catch so that a delivery
        // failure (after the session is already revoked) produces a clear error
        // message rather than the generic "Invalid email or password" from the
        // outer catch.
        const callbackURL = redirectTo
          ?? (role === "DESIGNER" ? "/studio/inbox" : "/trade/catalog");
        try {
          await (auth as any).api.signInMagicLink({
            body: { email, callbackURL, errorCallbackURL: "/sign-in?error=magic-link-expired" },
            headers: request.headers,
          });
        } catch {
          return data(
            { error: "Could not send verification email. Please try again.", intent },
            { status: 500 },
          );
        }

        return data({ requires2FA: true, email, intent: "2fa-pending" });
      }

      // No 2FA required — return session cookie as normal
      // Use getSetCookie() to preserve multiple Set-Cookie headers intact;
      // .get("set-cookie") would comma-join them into one malformed header.
      const cookies: string[] =
        typeof (authResponse.headers as any).getSetCookie === "function"
          ? (authResponse.headers as any).getSetCookie()
          : [authResponse.headers.get("set-cookie")].filter(Boolean);
      const destination = redirectTo ?? "/trade/catalog";
      const sep = destination.includes("?") ? "&" : "?";
      const responseHeaders = new Headers();
      for (const cookie of cookies) responseHeaders.append("Set-Cookie", cookie);
      return redirect(`${destination}${sep}_event=user_signed_in`, {
        headers: responseHeaders,
      });
    } catch {
      return data({ error: "Invalid email or password. Please try again.", intent }, { status: 401 });
    }
  }

  if (intent === "sign-up") {
    const email = String(formData.get("email") ?? "");
    const password = String(formData.get("password") ?? "");
    const displayName = String(formData.get("displayName") ?? "");
    const signUpRole = String(formData.get("signUpRole") ?? "B2B_BUYER");
    if (!email || !password || !displayName) {
      return data({ error: "All fields are required.", intent }, { status: 400 });
    }
    if (password.length < 8) {
      return data({ error: "Password must be at least 8 characters.", intent }, { status: 400 });
    }
    try {
      const role = signUpRole === "DESIGNER" ? "DESIGNER" : "B2B_BUYER";
      const authResponse = await (auth as any).api.signUpEmail({
        body: { email, password, name: displayName, role },
        headers: request.headers,
        asResponse: true,
      });
      if (!authResponse.ok) {
        const body = await authResponse.json().catch(() => ({}));
        const msg = (body as any)?.message ?? "Email already registered or invalid.";
        return data({ error: msg, intent }, { status: 400 });
      }
      // Use getSetCookie() to preserve multiple Set-Cookie headers intact;
      // .get("set-cookie") would comma-join them into one malformed header.
      const cookies: string[] =
        typeof (authResponse.headers as any).getSetCookie === "function"
          ? (authResponse.headers as any).getSetCookie()
          : [authResponse.headers.get("set-cookie")].filter(Boolean);
      const onboardingBase = role === "DESIGNER"
        ? "/inspire/creator-onboarding?_event=user_signed_up"
        : "/onboarding/buyer?_event=user_signed_up";
      const onboardingUrl = redirectTo
        ? `${onboardingBase}&redirect=${encodeURIComponent(redirectTo)}`
        : onboardingBase;
      const responseHeaders = new Headers();
      for (const cookie of cookies) responseHeaders.append("Set-Cookie", cookie);
      return redirect(onboardingUrl, {
        headers: responseHeaders,
      });
    } catch {
      return data({ error: "Could not create account. Please try again.", intent }, { status: 500 });
    }
  }

  if (intent === "resend-magic-link") {
    const email = String(formData.get("email") ?? "");
    if (!email) {
      return data({ error: "Email is required.", intent }, { status: 400 });
    }
    try {
      const callbackURL = redirectTo ?? "/trade/catalog";
      await (auth as any).api.signInMagicLink({
        body: { email, callbackURL, errorCallbackURL: "/sign-in?error=magic-link-expired" },
        headers: request.headers,
      });
      return data({ requires2FA: true, email, intent: "2fa-pending", resent: true });
    } catch {
      return data({ error: "Could not send verification link. Please try again.", intent }, { status: 500 });
    }
  }

  return data({ error: "Unknown action.", intent: "" }, { status: 400 });
}

// ─── Field component ───────────────────────────────────────────────────────
function Field({
  label,
  name,
  type = "text",
  placeholder,
  autoComplete,
  required,
}: {
  label: string;
  name: string;
  type?: string;
  placeholder?: string;
  autoComplete?: string;
  required?: boolean;
}) {
  return (
    <div className="space-y-1.5">
      <label
        htmlFor={name}
        className="block issue-label"
        style={{ fontFamily: "var(--font-sans)" }}
      >
        {label}
      </label>
      <input
        id={name}
        name={name}
        type={type}
        autoComplete={autoComplete}
        required={required}
        placeholder={placeholder}
        className="w-full border border-border bg-background text-foreground px-4 py-3 outline-none transition-colors focus:border-foreground/40 placeholder:text-muted-foreground/50"
        style={{ fontSize: "0.9rem", fontFamily: "var(--font-sans)" }}
      />
    </div>
  );
}

export default function SignInPage({ actionData, loaderData }: Route.ComponentProps) {
  const isDesigner = loaderData?.isDesigner ?? false;
  const demoEnabled = loaderData?.demoEnabled ?? false;
  const redirectTo = loaderData?.redirectTo ?? null;
  const navigation = useNavigation();
  const isSubmitting = navigation.state === "submitting";
  const [tab, setTab] = useState<"sign-in" | "sign-up">(
    actionData?.intent === "sign-up" ? "sign-up" : isDesigner ? "sign-up" : "sign-in",
  );
  const error = actionData && "error" in actionData ? actionData.error : undefined;
  const requires2FA = actionData && "requires2FA" in actionData ? actionData.requires2FA === true : false;
  const pendingEmail = actionData && "email" in actionData ? (actionData.email as string) : "";
  const resent = actionData && "resent" in actionData ? actionData.resent === true : false;
  const urlError = loaderData?.errorParam;
  const displayError = error ?? (urlError === "magic-link-expired" ? "Your verification link has expired. Please sign in again." : urlError === "invalid-token" ? "Invalid verification link. Please sign in again." : null);

  return (
    <div
      data-theme="client"
      className="min-h-screen bg-background text-foreground flex flex-col"
    >
      {/* Ambient BrilliantCut background */}
      <div className="fixed inset-0 flex items-center justify-center pointer-events-none" aria-hidden="true">
        <BrilliantCut size={900} opacity={0.032} animated />
      </div>

      {/* Back to home */}
      <div className="relative px-6 lg:px-12 pt-7">
        <a
          href="/"
          className="issue-label transition-opacity hover:opacity-70"
          style={{ fontFamily: "var(--font-sans)" }}
        >
          ← Caratzon
        </a>
      </div>

      {/* Main card */}
      <div className="relative flex-1 flex items-center justify-center px-6 py-16">
        <div className="w-full max-w-md">

          {/* Brand mark */}
          <div className="text-center mb-10">
            <a href="/" className="inline-flex flex-col items-center gap-4">
              <CaratzonLogo size={56} className="opacity-90" />
              <span
                className="masthead-wordmark text-foreground"
                style={{ fontSize: "clamp(1.6rem, 3vw, 2.2rem)", letterSpacing: "0.12em" }}
              >
                Caratzon
              </span>
            </a>
            <p
              className="mt-3 issue-label"
              style={{ fontFamily: "var(--font-sans)", opacity: 0.5 }}
            >
              {isDesigner ? "Designer Studio · Create Your Account" : "Trade Platform · Retail Buyer Access"}
            </p>
          </div>

          <div className="rule mb-8" />

          {/* Demo access */}
          {demoEnabled && (
          <div className="mb-8">
            <a
              href="/demo"
              className="group flex w-full items-center justify-between border px-5 py-4 transition-colors hover:border-foreground/30"
              style={{ borderColor: "var(--border)" }}
            >
              <div>
                <p
                  className="font-display font-light text-foreground leading-snug"
                  style={{ fontSize: "1rem" }}
                >
                  Try a Demo
                </p>
                <p
                  className="issue-label mt-0.5"
                  style={{ fontFamily: "var(--font-sans)", opacity: 0.45 }}
                >
                  Brand buyer or designer — no account required
                </p>
              </div>
              <span
                className="issue-label shrink-0 ml-4 transition-colors group-hover:opacity-100"
                style={{
                  fontFamily: "var(--font-sans)",
                  color: "var(--teal-sapphire)",
                  opacity: 0.85,
                  fontSize: "0.68rem",
                  letterSpacing: "0.2em",
                }}
              >
                CHOOSE DEMO →
              </span>
            </a>
          </div>
          )}

          {/* Divider */}
          <div className="flex items-center gap-3 mb-8">
            <div className="flex-1 rule" />
            <span className="issue-label shrink-0" style={{ fontFamily: "var(--font-sans)", opacity: 0.35 }}>
              or sign in
            </span>
            <div className="flex-1 rule" />
          </div>

          {/* 2FA: Check your email */}
          {requires2FA && (
            <div className="space-y-6">
              <div className="text-center space-y-3">
                <div
                  className="mx-auto flex items-center justify-center w-16 h-16 border border-border"
                  style={{ fontSize: "1.8rem" }}
                >
                  ✉
                </div>
                <h2
                  className="font-display font-light"
                  style={{ fontSize: "1.2rem" }}
                >
                  Check your email
                </h2>
                <p
                  className="text-muted-foreground"
                  style={{ fontSize: "0.82rem", fontFamily: "var(--font-sans)", lineHeight: 1.6 }}
                >
                  We sent a verification link to{" "}
                  <strong className="text-foreground">{pendingEmail}</strong>.
                  <br />
                  Click the link to complete sign-in. It expires in 5 minutes.
                </p>
                {resent && (
                  <p
                    style={{
                      fontSize: "0.78rem",
                      fontFamily: "var(--font-sans)",
                      color: "var(--teal-sapphire)",
                    }}
                  >
                    A new link has been sent.
                  </p>
                )}
              </div>

              <form method="post" className="space-y-3">
                <input type="hidden" name="intent" value="resend-magic-link" />
                <input type="hidden" name="email" value={pendingEmail} />
                {redirectTo && <input type="hidden" name="redirect" value={redirectTo} />}
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full py-3 text-center border border-border transition-colors hover:border-foreground/30 disabled:opacity-50 disabled:cursor-not-allowed"
                  style={{
                    fontFamily: "var(--font-sans)",
                    fontSize: "0.72rem",
                    letterSpacing: "0.18em",
                    textTransform: "uppercase",
                  }}
                >
                  {isSubmitting ? "Sending…" : "Resend Verification Link"}
                </button>
              </form>

              <div className="text-center">
                <a
                  href="/sign-in"
                  className="issue-label transition-opacity hover:opacity-70"
                  style={{ fontFamily: "var(--font-sans)", color: "var(--teal-sapphire)" }}
                >
                  ← Use a different account
                </a>
              </div>
            </div>
          )}

          {!requires2FA && (
            <>
              {/* Tab switcher */}
              <div className="flex items-center gap-0 mb-8 border border-border">
                {(["sign-in", "sign-up"] as const).map((t) => (
                  <button
                    key={t}
                    type="button"
                    onClick={() => setTab(t)}
                    className="flex-1 py-2.5 text-center transition-colors"
                    style={{
                      fontFamily: "var(--font-sans)",
                      fontSize: "0.7rem",
                      letterSpacing: "0.2em",
                      textTransform: "uppercase",
                      background: tab === t ? "var(--teal-sapphire)" : "transparent",
                      color: tab === t ? "var(--teal-sapphire-fg, #fff)" : "var(--foreground)",
                      opacity: tab === t ? 1 : 0.5,
                    }}
                  >
                    {t === "sign-in" ? "Sign In" : "Create Account"}
                  </button>
                ))}
              </div>

              {/* Error banner */}
              {displayError ? (
                <div
                  className="mb-6 px-4 py-3 border border-destructive/40 bg-destructive/8"
                  style={{ fontSize: "0.8rem", fontFamily: "var(--font-sans)" }}
                >
                  {displayError}
                </div>
              ) : null}

              {/* Sign-in form */}
              {tab === "sign-in" && (
                <form method="post" className="space-y-5">
                  <input type="hidden" name="intent" value="sign-in" />
                  {redirectTo && <input type="hidden" name="redirect" value={redirectTo} />}
                  <Field label="Email" name="email" type="email" autoComplete="email" required placeholder="you@company.com" />
                  <Field label="Password" name="password" type="password" autoComplete="current-password" required placeholder="••••••••" />
                  <div className="pt-2">
                    <button
                      type="submit"
                      disabled={isSubmitting}
                      className="w-full py-3.5 text-center transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
                      style={{
                        background: "var(--teal-sapphire)",
                        color: "var(--teal-sapphire-fg, #fff)",
                        fontFamily: "var(--font-sans)",
                        fontSize: "0.72rem",
                        letterSpacing: "0.22em",
                        textTransform: "uppercase",
                      }}
                    >
                      {isSubmitting ? "Signing In…" : "Sign In →"}
                    </button>
                  </div>
                  <p className="text-center" style={{ fontSize: "0.75rem", fontFamily: "var(--font-sans)" }}>
                    <span className="text-muted-foreground">No account yet? </span>
                    <button
                      type="button"
                      onClick={() => setTab("sign-up")}
                      className="transition-opacity hover:opacity-70"
                      style={{ color: "var(--teal-sapphire)" }}
                    >
                      {isDesigner ? "Create a designer account" : "Create a buyer account"} →
                    </button>
                  </p>
                </form>
              )}

              {/* Sign-up form */}
              {tab === "sign-up" && (
                <form method="post" className="space-y-5">
                  <input type="hidden" name="intent" value="sign-up" />
                  <input type="hidden" name="signUpRole" value={isDesigner ? "DESIGNER" : "B2B_BUYER"} />
                  {redirectTo && <input type="hidden" name="redirect" value={redirectTo} />}
                  <Field label="Full Name" name="displayName" autoComplete="name" required placeholder={isDesigner ? "Aria Atelier" : "Sarah Chen"} />
                  <Field label="Email" name="email" type="email" autoComplete="email" required placeholder="you@company.com" />
                  <Field label="Password" name="password" type="password" autoComplete="new-password" required placeholder="Min. 8 characters" />
                  <div className="pt-2">
                    <button
                      type="submit"
                      disabled={isSubmitting}
                      className="w-full py-3.5 text-center transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
                      style={{
                        background: "var(--teal-sapphire)",
                        color: "var(--teal-sapphire-fg, #fff)",
                        fontFamily: "var(--font-sans)",
                        fontSize: "0.72rem",
                        letterSpacing: "0.22em",
                        textTransform: "uppercase",
                      }}
                    >
                      {isSubmitting ? "Creating Account…" : "Create Account →"}
                    </button>
                  </div>
                  <p
                    className="text-center text-muted-foreground"
                    style={{ fontSize: "0.73rem", fontFamily: "var(--font-sans)", lineHeight: 1.6 }}
                  >
                    By creating an account, you agree to Caratzon's{" "}
                    <a href="/about" className="underline decoration-border hover:decoration-foreground transition-colors">
                      {isDesigner ? "Designer Terms" : "Buyer Terms"}
                    </a>
                    . {isDesigner ? "You'll set up your designer profile next." : "You'll complete a short business verification next."}
                  </p>
                  <p className="text-center" style={{ fontSize: "0.75rem", fontFamily: "var(--font-sans)" }}>
                    <span className="text-muted-foreground">Already have an account? </span>
                    <button
                      type="button"
                      onClick={() => setTab("sign-in")}
                      className="transition-opacity hover:opacity-70"
                      style={{ color: "var(--teal-sapphire)" }}
                    >
                      Sign in →
                    </button>
                  </p>
                  <p className="text-center" style={{ fontSize: "0.75rem", fontFamily: "var(--font-sans)" }}>
                    <a
                      href={isDesigner ? "/sign-in" : "/sign-in?role=designer"}
                      className="transition-opacity hover:opacity-70"
                      style={{ color: "var(--teal-sapphire)" }}
                    >
                      {isDesigner ? "Create a buyer account →" : "Create a designer account →"}
                    </a>
                  </p>
                </form>
              )}
            </>
          )}

          <div className="rule mt-10 mb-6" />

          {/* Marketing zone links */}
          <div className="flex items-center justify-center gap-6">
            {[
              { href: "/brands", label: "For Brands" },
              { href: "/brands/pricing", label: "View Pricing" },
              { href: "/about", label: "About" },
            ].map((link) => (
              <a
                key={link.href}
                href={link.href}
                className="issue-label transition-opacity hover:opacity-70"
                style={{ fontFamily: "var(--font-sans)", opacity: 0.4 }}
              >
                {link.label}
              </a>
            ))}
          </div>

        </div>
      </div>
    </div>
  );
}
